export const STAFF_HOME_PATH = "/staff";

const MAX_RETURN_PATH_LENGTH = 1024;
const DUMMY_ORIGIN = "https://motusbugs.invalid";

function fallbackStaffPath(basePath = ""): string {
  return `${basePath}${STAFF_HOME_PATH}`;
}

function isPathUnderStaff(pathname: string, basePath: string): boolean {
  const roots =
    basePath && basePath !== "/"
      ? [`${basePath}${STAFF_HOME_PATH}`, STAFF_HOME_PATH]
      : [STAFF_HOME_PATH];
  return roots.some((root) => pathname === root || pathname.startsWith(`${root}/`));
}

/**
 * Accept only a same-origin relative path under `/staff`.
 *
 * Rejects absolute URLs, protocol-relative `//host` values, backslash tricks,
 * and anything outside `/staff`. Missing or invalid values fall back to `/staff`
 * (prefixed with `basePath` when the SPA is mounted on a subpath).
 */
export function staffReturnPath(raw: unknown, basePath = ""): string {
  const fallback = fallbackStaffPath(basePath);
  if (typeof raw !== "string") return fallback;

  const value = raw.trim();
  if (!value || value.length > MAX_RETURN_PATH_LENGTH) return fallback;

  // Browsers treat `\` as `/`, so `/\\evil` becomes protocol-relative `//evil`.
  if (value.includes("\\") || /%5c/i.test(value)) return fallback;
  if (/[\u0000-\u001f\u007f]/.test(value)) return fallback;

  if (!value.startsWith("/") || value.startsWith("//")) return fallback;

  let url: URL;
  try {
    url = new URL(value, DUMMY_ORIGIN);
  } catch {
    return fallback;
  }

  if (url.origin !== DUMMY_ORIGIN) return fallback;
  if (url.username || url.password) return fallback;

  const { pathname } = url;
  if (!pathname.startsWith("/") || pathname.startsWith("//")) return fallback;
  if (pathname.split("/").includes("..")) return fallback;
  if (!isPathUnderStaff(pathname, basePath)) return fallback;

  const result = `${pathname}${url.search}${url.hash}`;
  if (!result.startsWith("/") || result.startsWith("//") || result.includes("\\")) {
    return fallback;
  }
  return result;
}

/** Wouter path for the signed-out staff gate: `/sign-in?redirect_url=…`. */
export function staffSignInLocation(path: string, search = "", basePath = ""): string {
  const candidate = `${basePath}${path}${search}`;
  return `/sign-in?redirect_url=${encodeURIComponent(staffReturnPath(candidate, basePath))}`;
}

/** Clerk `forceRedirectUrl` from the sign-in page's `redirect_url` query param. */
export function staffForceRedirectUrl(search: string, basePath = ""): string {
  const query = search.startsWith("?") ? search.slice(1) : search;
  return staffReturnPath(new URLSearchParams(query).get("redirect_url"), basePath);
}
